import { Alert, Button, Card, CardBody, Input, Select } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './settings.module.css'
import { AppLayout } from '@/components/AppLayout'
import { getAuthState, isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/settings')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: SettingsPage,
})

const timezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Shanghai', label: 'Shanghai' },
]

function SettingsPage() {
  const auth = getAuthState()
  const [activeTab, setActiveTab] = useState<'profile' | 'notifications' | 'security'>('profile')
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: auth.user?.name ?? '',
    email: auth.user?.email ?? '',
    timezone: 'UTC',
  })

  // Notification form state
  const [notificationData, setNotificationData] = useState({
    emailEnabled: true,
    slackEnabled: false,
    slackWebhook: '',
    digestFrequency: 'daily',
  })

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordError, setPasswordError] = useState<string | null>(null)

  const handleSaveProfile = (e: React.FormEvent): void => {
    e.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)

    // Simulate API call
    setTimeout(() => {
      setIsSaving(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    }, 1000)
  }

  const handleSaveNotifications = (e: React.FormEvent): void => {
    e.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)

    setTimeout(() => {
      setIsSaving(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    }, 1000)
  }

  const handleChangePassword = (e: React.FormEvent): void => {
    e.preventDefault()
    setPasswordError(null)

    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setIsSaving(true)
    setSaveSuccess(false)

    setTimeout(() => {
      setIsSaving(false)
      setSaveSuccess(true)
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setTimeout(() => setSaveSuccess(false), 3000)
    }, 1000)
  }

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: '👤' },
    { id: 'notifications' as const, label: 'Notifications', icon: '🔔' },
    { id: 'security' as const, label: 'Security', icon: '🔒' },
  ]

  return (
    <AppLayout title="Settings">
      <div className={styles.container}>
        {/* Tabs */}
        <div className={styles.tabs}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Success Alert */}
        {saveSuccess && (
          <Alert variant="success" className={styles.successAlert}>
            Settings saved successfully!
          </Alert>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <Card>
            <CardBody>
              <h2 className={styles.sectionTitle}>Profile Settings</h2>
              <p className={styles.sectionDescription}>
                Update your personal information and preferences.
              </p>

              <form className={styles.form} onSubmit={handleSaveProfile}>
                <Input
                  label="Name"
                  type="text"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  placeholder="Your name"
                />

                <Input
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  placeholder="you@example.com"
                  disabled
                />
                <p className={styles.fieldHint}>Email cannot be changed.</p>

                <Select
                  label="Default Timezone"
                  value={profileData.timezone}
                  onChange={(e) => setProfileData({ ...profileData, timezone: e.target.value })}
                  options={timezones}
                />

                <div className={styles.formActions}>
                  <Button type="submit" variant="primary" loading={isSaving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card>
            <CardBody>
              <h2 className={styles.sectionTitle}>Notification Settings</h2>
              <p className={styles.sectionDescription}>
                Configure how and when you receive pricing change alerts.
              </p>

              <form className={styles.form} onSubmit={handleSaveNotifications}>
                <div className={styles.toggleGroup}>
                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.emailEnabled}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            emailEnabled: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Email Notifications</span>
                        <span className={styles.toggleDescription}>
                          Receive alerts via email when pricing changes are detected
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className={styles.toggle}>
                    <label className={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={notificationData.slackEnabled}
                        onChange={(e) =>
                          setNotificationData({
                            ...notificationData,
                            slackEnabled: e.target.checked,
                          })
                        }
                      />
                      <span className={styles.toggleSwitch} />
                      <div className={styles.toggleText}>
                        <span className={styles.toggleTitle}>Slack Notifications</span>
                        <span className={styles.toggleDescription}>
                          Send pricing change alerts to a Slack channel
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {notificationData.slackEnabled && (
                  <Input
                    label="Slack Webhook URL"
                    type="url"
                    value={notificationData.slackWebhook}
                    onChange={(e) =>
                      setNotificationData({ ...notificationData, slackWebhook: e.target.value })
                    }
                    placeholder="https://hooks.slack.com/services/..."
                  />
                )}

                <Select
                  label="Daily Digest"
                  value={notificationData.digestFrequency}
                  onChange={(e) =>
                    setNotificationData({ ...notificationData, digestFrequency: e.target.value })
                  }
                  options={[
                    { value: 'none', label: 'Disabled' },
                    { value: 'daily', label: 'Daily at 9am' },
                    { value: 'weekly', label: 'Weekly on Monday' },
                  ]}
                />

                <div className={styles.formActions}>
                  <Button type="submit" variant="primary" loading={isSaving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <>
            <Card className={styles.securityCard}>
              <CardBody>
                <h2 className={styles.sectionTitle}>Change Password</h2>
                <p className={styles.sectionDescription}>
                  Update your password to keep your account secure.
                </p>

                {passwordError && (
                  <Alert variant="danger" className={styles.alert}>
                    {passwordError}
                  </Alert>
                )}

                <form className={styles.form} onSubmit={handleChangePassword}>
                  <Input
                    label="Current Password"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, currentPassword: e.target.value })
                    }
                    placeholder="Enter current password"
                    required
                  />

                  <Input
                    label="New Password"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    placeholder="At least 8 characters"
                    required
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    placeholder="Confirm new password"
                    required
                  />

                  <div className={styles.formActions}>
                    <Button type="submit" variant="primary" loading={isSaving}>
                      Update Password
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>

            <Card className={styles.dangerCard}>
              <CardBody>
                <h2 className={styles.dangerTitle}>Danger Zone</h2>
                <p className={styles.dangerDescription}>
                  Once you delete your account, there is no going back. Please be certain.
                </p>
                <Button variant="danger">Delete Account</Button>
              </CardBody>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  )
}
